import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DropResult,
} from "@hello-pangea/dnd";
import { MenuIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { DatabaseIcon, VariableIcon, ZapIcon } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import EditRegistryToolDialog from "./EditRegistryToolDialog";
import { FunctionDefinitionBox } from "./FunctionDefinitionBox";
import SaveToRegistryPopover from "./SaveToRegistryPopover";

import ChooseEntityDialogTrigger from "@/components/ChooseEntityDialog/ChooseEntityDialogTrigger";
import { useFunctionDialogContext } from "@/components/FunctionsModal";
import { usePlayground } from "@/components/Playground/playground-store";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { EntityType } from "@/queries/folder-entities/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { cn } from "@/lib/utils";
import { CacheControl, CustomFunction_, Function_, RegistryTool, ToolVariable } from "@/types";
import { isBuiltInTool, isMcpTool, isToolVariable } from "@/utils/utils";
import { useUser } from "@/context/user-context";

const isRegistryTool = (tool: unknown): tool is RegistryTool =>
  typeof tool === "object" &&
  tool !== null &&
  "type" in tool &&
  (tool as RegistryTool).type === "registry";

const CACHE_OPTIONS: { label: string; value: CacheControl | undefined }[] = [
  { label: "No caching", value: undefined },
  { label: "Cache (5min)", value: { type: "ephemeral" } },
  { label: "Cache (1hr)", value: { type: "ephemeral", ttl: "1h" } },
];

function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

const ToolVariableElement = ({
  toolVariable,
  draggableIcon,
  onRemove,
}: {
  toolVariable: ToolVariable;
  draggableIcon: ReactNode;
  onRemove: () => void;
}) => {
  const { isEditing } = useFunctionDialogContext();

  return (
    <div className="flex items-center gap-x-4 pb-2">
      {isEditing ? draggableIcon : null}
      <div className="flex flex-1 items-center gap-2 rounded-md border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
        <VariableIcon className="h-4 w-4 text-blue-500" />
        <span className="text-sm font-medium text-blue-700">Tool Variable:</span>
        <code className="rounded bg-blue-100 px-1.5 py-0.5 text-sm text-blue-800">
          {toolVariable.name}
        </code>
        <span className="text-xs text-blue-500">
          (tools injected at runtime from input variable)
        </span>
      </div>
      {isEditing ? (
        <button onClick={onRemove} className="text-red-400 hover:text-red-500">
          <XIcon className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};

const RegistryToolElement = ({
  registryTool,
  draggableIcon,
  onRemove,
  onReplace,
}: {
  registryTool: RegistryTool;
  draggableIcon: ReactNode;
  onRemove: () => void;
  onReplace: (next: RegistryTool) => void;
}) => {
  const { isEditing } = useFunctionDialogContext();
  const playground = usePlayground();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;
  const resolvedDef = playground.getRegistryToolName(registryTool.tool_registry_id);
  const displayName =
    registryTool._resolved_name ?? resolvedDef ?? `Tool #${registryTool.tool_registry_id}`;

  const toolUrl = workspaceId
    ? `/workspace/${workspaceId}/tool/${registryTool.tool_registry_id}`
    : undefined;

  const entity = useMemo(
    () => ({
      id: registryTool.tool_registry_id,
      name: displayName,
      type: EntityType.TOOL as const,
      path: [] as string[],
      label: registryTool.label ?? undefined,
      version_number: registryTool.version_number ?? undefined,
    }),
    [registryTool.tool_registry_id, displayName, registryTool.label, registryTool.version_number],
  );

  const willAutoExecute = registryTool._has_execution ?? false;
  const [isEditOpen, setIsEditOpen] = useState(false);

  return (
    <div className="flex items-center gap-x-4 pb-2">
      {isEditing ? draggableIcon : null}
      <div className="flex-1">
        <ChooseEntityDialogTrigger
          type={EntityType.TOOL}
          selectedEntity={entity}
          readonly
          url={toolUrl}
          trailingBadge={
            willAutoExecute ? (
              <BasicTooltip content="PromptLayer will run this tool automatically.">
                {/* role="img" + aria-label on the wrapper because some screen readers ignore aria-label on a bare svg without an explicit role. */}
                <span role="img" aria-label="Auto-executes" className="inline-flex">
                  <ZapIcon className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
                </span>
              </BasicTooltip>
            ) : undefined
          }
        />
      </div>
      {isEditing ? (
        <button
          onClick={() => setIsEditOpen(true)}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Edit version or label"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ) : null}
      {isEditing ? (
        <button onClick={onRemove} className="text-red-400 hover:text-red-500">
          <XIcon className="h-4 w-4" />
        </button>
      ) : null}
      <EditRegistryToolDialog
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        registryTool={registryTool}
        displayName={displayName}
        onSave={onReplace}
      />
    </div>
  );
};

const FunctionElement = ({
  _function,
  draggableIcon,
  onNavigateToFunction,
  onSetCacheControl,
  isAnthropicProvider,
  onReplaceWithRegistry,
}: {
  _function: Function_;
  draggableIcon: ReactNode;
  onNavigateToFunction: () => void;
  onSetCacheControl?: (cc: CacheControl | undefined) => void;
  isAnthropicProvider?: boolean;
  onReplaceWithRegistry?: (registryTool: RegistryTool) => void;
}) => {
  const { isEditing, setEditingFunction } = useFunctionDialogContext();

  const handleEditClick = () => {
    const plainFunction = JSON.parse(JSON.stringify(_function));
    setEditingFunction(plainFunction);
  };

  const cacheControl =
    "cache_control" in _function ? (_function as CustomFunction_).cache_control : undefined;
  const cacheLabel = getCacheLabel(cacheControl);
  const functionName = isMcpTool(_function)
    ? (_function._resolved_name ?? "MCP Server")
    : _function.name;

  return (
    <div className="flex items-center gap-x-4 pb-2">
      {isEditing ? draggableIcon : null}
      <div className="flex-1">
        <FunctionDefinitionBox functionName={functionName} functionDeclaration={_function} />
      </div>
      {isAnthropicProvider && isEditing && onSetCacheControl && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              title="Prompt cache breakpoint"
              className={cn(
                "flex min-w-[56px] items-center justify-center gap-0.5 rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                cacheLabel
                  ? "border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100"
                  : "border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
              )}
              aria-label="Cache control"
            >
              <DatabaseIcon className="h-3 w-3" />
              {cacheLabel ?? "Cache"}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[140px]">
            {CACHE_OPTIONS.map(opt => (
              <DropdownMenuItem
                key={opt.label}
                onSelect={() => onSetCacheControl(opt.value)}
                className={cn(
                  "text-xs",
                  JSON.stringify(cacheControl) === JSON.stringify(opt.value) && "font-semibold",
                )}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {isAnthropicProvider && !isEditing && cacheLabel && (
        <BasicTooltip content="Prompt cache breakpoint">
          <span className="flex min-w-[56px] items-center justify-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
            <DatabaseIcon className="h-3 w-3" />
            {cacheLabel}
          </span>
        </BasicTooltip>
      )}
      {isEditing ? (
        <>
          {"name" in _function &&
            !isBuiltInTool(_function) &&
            !isMcpTool(_function) &&
            onReplaceWithRegistry && (
              <SaveToRegistryPopover
                functionDef={_function as CustomFunction_}
                onReplace={onReplaceWithRegistry}
              />
            )}
          <button onClick={handleEditClick} className="text-gray-600 hover:text-gray-500">
            <PencilIcon className="h-4 w-4" />
          </button>
          <button onClick={onNavigateToFunction} className="text-red-400 hover:text-red-500">
            <XIcon className="h-4 w-4" />
          </button>
        </>
      ) : null}
    </div>
  );
};

export const FunctionsList = ({
  isAnthropicProvider = false,
}: {
  isAnthropicProvider?: boolean;
}) => {
  const { functions, setFunctions } = useFunctionDialogContext();
  const countFunctions = functions?.length || 0;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const items = Array.from(functions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    if (setFunctions !== undefined) {
      setFunctions(items);
    }
  };

  const handleSetCacheControl = (index: number, cc: CacheControl | undefined) => {
    if (!setFunctions) {
      return;
    }
    const updated = [...functions];
    const func = { ...updated[index] } as CustomFunction_;
    func.cache_control = cc;
    updated[index] = func;
    setFunctions(updated);
  };

  if (countFunctions === 0) {
    return <div className="pb-3 pt-1 text-sm italic text-gray-500">No functions defined</div>;
  }
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="functions">
        {(provided: DroppableProvided) => (
          <div {...provided.droppableProps} ref={provided.innerRef} className="py-2">
            {(functions || []).map((_function, index) => {
              const isVariable = isToolVariable(_function as unknown as ToolVariable);
              const isRegistry = isRegistryTool(_function);
              const getDraggableId = () => {
                if (isVariable) {
                  return `var-${(_function as unknown as ToolVariable).name}-${index}`;
                }
                if (isRegistry) {
                  return `reg-${(_function as RegistryTool).tool_registry_id}-${index}`;
                }
                if (isMcpTool(_function)) {
                  return `mcp-${_function.mcp_server_id}-${index}`;
                }
                return `${_function.name}-${index}`;
              };
              const draggableId = getDraggableId();
              return (
                <Draggable key={draggableId} draggableId={draggableId} index={index}>
                  {(draggableProvided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                    const draggableContent = (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        {(() => {
                          const dragIcon = (
                            <div {...draggableProvided.dragHandleProps}>
                              <MenuIcon className="h-4 w-4 cursor-pointer text-gray-400" />
                            </div>
                          );
                          const removeHandler = () =>
                            setFunctions!(functions.filter((_, i) => i !== index));
                          const replaceRegistryHandler = (next: RegistryTool) => {
                            const updated = [...functions];
                            updated[index] = next as unknown as Function_;
                            setFunctions!(updated);
                          };

                          if (isVariable) {
                            return (
                              <ToolVariableElement
                                toolVariable={_function as unknown as ToolVariable}
                                onRemove={removeHandler}
                                draggableIcon={dragIcon}
                              />
                            );
                          }

                          if (isRegistry) {
                            return (
                              <RegistryToolElement
                                registryTool={_function as unknown as RegistryTool}
                                onRemove={removeHandler}
                                onReplace={replaceRegistryHandler}
                                draggableIcon={dragIcon}
                              />
                            );
                          }

                          return (
                            <FunctionElement
                              _function={_function}
                              onNavigateToFunction={removeHandler}
                              onSetCacheControl={cc => handleSetCacheControl(index, cc)}
                              isAnthropicProvider={isAnthropicProvider}
                              draggableIcon={dragIcon}
                              onReplaceWithRegistry={regTool => {
                                if (setFunctions) {
                                  const newFunctions = [...functions];
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  newFunctions[index] = regTool as any;
                                  setFunctions(newFunctions);
                                }
                              }}
                            />
                          );
                        })()}
                      </div>
                    );

                    if (snapshot.isDragging) {
                      return createPortal(draggableContent, document.body);
                    }

                    return draggableContent;
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
